import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../../lib/apiClient';

// Smart suggestions (signals) and capability suggestions derived from
// purchase history, fetched with a 300ms debounce on department switch.
export function useZtaSuggestions(selectedDepartment) {
  // Smart Suggestions state (for signals)
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const suggestionsDebounceRef = useRef(null);

  // Capability Suggestions state
  const [capabilitySuggestions, setCapabilitySuggestions] = useState(null);

  // Fetch smart suggestions based on purchase history
  const fetchSuggestions = useCallback(async (skipCache = false) => {
    if (!selectedDepartment?.department_id) return;

    setSuggestionsLoading(true);
    setSuggestionsError(null);
    try {
      const url = `/api/zero-trust/departments/${selectedDepartment.department_id}/suggestions${skipCache ? '?refresh=true' : ''}`;
      const data = await apiClient.get(url);
      setSuggestions(data);
      console.log('📊 ZT Suggestions loaded:', data);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setSuggestionsError(err.message || 'Failed to load suggestions');
      setSuggestions(null);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [selectedDepartment?.department_id]);

  // Fetch capability suggestions based on purchase history
  const fetchCapabilitySuggestions = useCallback(async (skipCache = false) => {
    if (!selectedDepartment?.department_id) return;

    try {
      const url = `/api/zero-trust/departments/${selectedDepartment.department_id}/capability-suggestions${skipCache ? '?refresh=true' : ''}`;
      const data = await apiClient.get(url);
      setCapabilitySuggestions(data);
      console.log('📊 Capability Suggestions loaded:', data);
    } catch (err) {
      console.error('Error fetching capability suggestions:', err);
      setCapabilitySuggestions(null);
    }
  }, [selectedDepartment?.department_id]);

  // Fetch suggestions when department changes (with debounce)
  useEffect(() => {
    // Clear any pending debounce
    if (suggestionsDebounceRef.current) {
      clearTimeout(suggestionsDebounceRef.current);
    }

    if (selectedDepartment?.department_id) {
      // Debounce to prevent rapid API calls during department switching
      suggestionsDebounceRef.current = setTimeout(() => {
        fetchSuggestions();
        fetchCapabilitySuggestions();
      }, 300); // 300ms debounce
    } else {
      setSuggestions(null);
      setSuggestionsError(null);
      setCapabilitySuggestions(null);
    }

    return () => {
      if (suggestionsDebounceRef.current) {
        clearTimeout(suggestionsDebounceRef.current);
      }
    };
  }, [selectedDepartment?.department_id, fetchSuggestions, fetchCapabilitySuggestions]);

  return {
    suggestions,
    suggestionsLoading,
    suggestionsError,
    fetchSuggestions,
    capabilitySuggestions,
  };
}
